import {
  getMonths,
  getTableData,
  getTotalByKey
} from '@features/groups/utils/groupReports'
import SafeguardingBanner from '@features/reports/components/incidents/SafeguardingBanner'
import { colors } from '@lmc/common/constants'
import { SAFEGUARDING_ITEM_ID } from '@lmc/common/types'
import type { Carer } from '@lmc/ts-common'
import useSafeguardingAccess from 'components/AccessControl/useSafeguardingAccess'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHasSafeguardingEnabled from 'hooks/useHasSafeguardingEnabled'
import { filter, includes, map, orderBy } from 'lodash'
import PropTypes from 'prop-types'
import type React from 'react'
import { type CSSProperties, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { isGroupUser } from 'utils'
import { BarChart } from '../charts'
import PropertyFilters from '../filters/components/PropertyFilters'
import {
  Checkbox,
  HeaderSection,
  NoDataFound,
  Spinner,
  Table
} from '../generic'

const DISPLAY_DATE_FORMAT = 'MMM'
const DATA_DATE_FORMAT = 'MM/YYYY'

const renderEmpty = (emptyText: string) => (
  <div style={{ paddingTop: 5 }}>
    {/* // @ts-ignore */}
    <NoDataFound
      heading="Nothing to see here..."
      text={emptyText || 'Your group has no Incidents to display.'}
      url=""
      linkText=""
    />
  </div>
)

const renderHeader = (
  data: any[],
  stacked: boolean | null | undefined,
  setStackChart: {
    (value: React.SetStateAction<boolean>): void
    (arg0: boolean): any
  },
  activeFilters: { [x: string]: any },
  setActiveFilters: {
    (value: React.SetStateAction<{}>): void
    (arg0: { item: never[] }): void
  },
  title: any,
  backLink: string | null | undefined,
  safeguardingAccess: boolean | undefined
) => {
  const filteredData = safeguardingAccess
    ? data
    : data?.filter(
        (row: { keyDoc: { id: any; name: any } }) =>
          row?.keyDoc?.id !== SAFEGUARDING_ITEM_ID
      )

  const allFilters = {
    item: {
      label: 'Incident Type',
      filters:
        filteredData &&
        filteredData.map((row: { keyDoc: { id: any; name: any } }, i: any) => ({
          value: row.keyDoc ? row.keyDoc.id : `Custom`,
          label: row.keyDoc ? row.keyDoc.name : 'Custom'
        }))
    }
  }

  return (
    <div>
      <HeaderSection backLink={backLink} title={`${title}`} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingTop: 15
        }}
      >
        <div style={{ flex: 1 }}>
          <PropertyFilters
            activeFilters={activeFilters}
            allFilters={allFilters}
            // @ts-ignore
            onChange={({ key, value }) => {
              const exists = includes(activeFilters[key], value)
              if (!value.length) {
                // This handles clearing all filters
                setActiveFilters({ item: [] })
              } else {
                setActiveFilters({
                  ...activeFilters,
                  [key]: exists
                    ? filter(activeFilters[key], (i) => i !== value)
                    : [...(activeFilters[key] || []), value]
                })
              }
            }}
            expandedView
          />
        </div>
        <div style={{ flexGrow: 1 }} />
        <div style={{ flex: 1, maxWidth: 150 }}>
          <Checkbox
            label="Stack Chart"
            onChange={() => setStackChart(!stacked)}
            isChecked={stacked}
          />
        </div>
      </div>
    </div>
  )
}

const formatData = (
  data: any[],
  from: any,
  to: any,
  stacked: any,
  activeFilters: { item: string | any[] }
) => {
  const months = getMonths(from, to)
  const columns = [
    {
      label: 'Incident',
      sortable: false,
      width: 150
    },
    ...months.map((month) => ({
      label: month.format(DISPLAY_DATE_FORMAT),
      sortable: false,
      type: 'number',
      maxWidth: (styles.mainContainer.maxWidth - 150) / months.length
    }))
  ]

  let filteredData = data.filter(
    (row: { keyDoc: any; key: any }) =>
      !activeFilters.item ||
      !activeFilters.item.length ||
      activeFilters.item.includes(row.keyDoc ? row.key : 'Custom')
  )

  filteredData = orderBy(filteredData, (d) =>
    d.keyDoc ? d.keyDoc.name : d.key
  )

  const categories = months.map((month) => month.format(DISPLAY_DATE_FORMAT))
  const monthKeys = months.map((month) => ({
    key: month.format(DATA_DATE_FORMAT)
  }))
  const tableData = getTableData(filteredData, monthKeys)

  let series
  if (stacked) {
    series = tableData.map((row) => {
      const [name, ...data] = row
      // @ts-ignore
      const color = styles.incidentColours[name]
      return { name, data, color }
    })
  } else {
    const total = getTotalByKey(filteredData, map(monthKeys, 'key'))
    series = [
      {
        name: 'Total',
        color: colors.red,
        data: total.map((d: { value: any }) => d.value)
      }
    ]
  }

  return {
    series,
    tableData,
    categories,
    columns
  }
}

export const renderResult = (
  data: any,
  from: dayjs.Dayjs,
  to: dayjs.Dayjs,
  stacked: boolean,
  activeFilters: {},
  chartHeight: number | null | undefined,
  hasSafeguardingEnabled: boolean | undefined,
  safeguardingAccess: boolean | undefined
) => {
  const { series, tableData, categories, columns } = formatData(
    data,
    from,
    to,
    stacked,
    // @ts-ignore
    activeFilters
  )

  const displaySafeguardingBanner =
    hasSafeguardingEnabled && !safeguardingAccess

  return (
    <Container>
      {displaySafeguardingBanner && <SafeguardingBanner />}
      <BarChart
        // @ts-ignore
        yAxisLabel="Total Count"
        categories={categories}
        stacked={stacked}
        legend={stacked}
        series={series}
        height={chartHeight}
      />
      <Table
        data={tableData}
        columns={columns}
        defaultSorted={{ title: 'Incident', sorted: 'asc' }}
      />
    </Container>
  )
}

export function IncidentReport(props: {
  isLoading: any
  error: any
  data: any
  title: any
  backLink: any
  emptyText: any
  chartHeight: any
}) {
  const { isLoading, error, data, title, backLink, emptyText, chartHeight } =
    props

  // const [to, setToDate] = useState(dayjs())
  // const [from, setFromDate] = useState(dayjs().subtract(1, 'year').startOf('month'))
  const to = dayjs()
  const from = dayjs().subtract(1, 'year').startOf('month')
  const [activeFilters, setActiveFilters] = useState({})
  const [stacked, setStackChart] = useState(false)

  const { data: flags } = useFeatureFlags({
    flagNames: ['safeguarding'] as const
  })
  const isSafeguardingEnabled = flags?.safeguarding.enabled

  const user = useSelector(
    (state: { global: { user: Carer } }) => state.global.user
  )

  const isGroupUserRole = isGroupUser(user?.role)

  const hasSafeguardingHook = useHasSafeguardingEnabled()

  const hasSafeguardingEnabled =
    isSafeguardingEnabled && (isGroupUserRole || hasSafeguardingHook)
  const safeguardingAccess = useSafeguardingAccess()

  if (error) {
    return (
      // @ts-ignore
      <div style={styles.container as CSSProperties}>
        <h1>Oops! Something went wrong!</h1>
        <p>{error}</p>
      </div>
    )
  }

  return (
    <div>
      {renderHeader(
        data,
        stacked,
        setStackChart,
        activeFilters,
        setActiveFilters,
        title,
        backLink,
        safeguardingAccess
      )}
      {isLoading ? (
        <Spinner />
      ) : data && data.length ? (
        renderResult(
          data,
          from,
          to,
          stacked,
          activeFilters,
          chartHeight,
          hasSafeguardingEnabled,
          safeguardingAccess
        )
      ) : (
        renderEmpty(emptyText)
      )}
    </div>
  )
}

const styles = {
  dataContainer: {
    display: 'flex',
    width: '100%'
  },
  incidentColours: {
    'ABC Behaviour': colors.purple,
    Assault: colors.orange,
    Choking: colors.pink,
    Custom: colors.grey,
    Fall: colors.rockblue,
    'Food poisoning': colors.pink,
    Illness: colors.turquoise,
    Injury: colors.lightblue,
    Ingestion: colors.lightpink,
    Manual: colors.green,
    'Medication error': colors.blue,
    'Missing Person': colors.lavender,
    'Near Miss': colors.darkgrey,
    Scald: colors.darkblue,
    Seizure: colors.delugepurple,
    'Unexpected Death': colors.red
  },
  mainContainer: {
    margin: '0 auto',
    maxWidth: 960,
    padding: '30px 0px 80px 0px',
    position: 'relative'
  }
}

IncidentReport.propTypes = {
  chartHeight: PropTypes.number,
  data: PropTypes.array,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  title: PropTypes.string
}

export default IncidentReport

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`
